import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Smartphone, CheckCircle, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const InstallPrompt = ({ user }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState('unknown'); // 'ios', 'android', 'desktop'
  const [isInstalled, setIsInstalled] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (isStandalone) {
      setIsInstalled(true);
      markInstalled();
      return;
    }

    // Check localStorage
    if (localStorage.getItem('pwa_installed') === 'true') {
      setIsInstalled(true);
      return;
    }

    // Detect platform
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /Android/.test(ua);
    const isMobile = isiOS || isAndroid || /Mobile/.test(ua);
    
    // Only show on mobile devices
    if (!isMobile) {
      return; // Don't show on desktop at all
    }
    
    setPlatform(isiOS ? 'ios' : 'android');

    // Listen for install prompt (Chrome/Android)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      localStorage.setItem('pwa_installed', 'true');
      markInstalled();
    });

    // Show after short delay on mobile
    const timer = setTimeout(() => {
      if (!isStandalone && localStorage.getItem('pwa_installed') !== 'true') {
        setShowPrompt(true);
      }
    }, 2000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(timer);
    };
  }, []); // eslint-disable-line

  const markInstalled = async () => {
    try {
      await fetch(`${API}/api/users/me/pwa-installed`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
      });
    } catch (e) { console.error('PWA install tracking failed', e); }
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      setInstalling(true);
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setIsInstalled(true);
        setShowPrompt(false);
        localStorage.setItem('pwa_installed', 'true');
        markInstalled();
      }
      setDeferredPrompt(null);
      setInstalling(false);
    }
  };

  const dismissForNow = () => {
    setShowPrompt(false);
    sessionStorage.setItem('pwa_dismissed', 'true');
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[9998] flex items-end justify-center"
        style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} data-testid="install-prompt-overlay">
        <motion.div initial={{ y: 300 }} animate={{ y: 0 }} transition={{ type: 'spring', damping: 25 }}
          className="w-full max-w-md bg-white rounded-t-3xl overflow-hidden shadow-2xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-5 text-center">
            <div className="w-16 h-16 bg-white rounded-2xl mx-auto mb-3 flex items-center justify-center shadow-lg">
              <Smartphone className="w-8 h-8 text-orange-500" />
            </div>
            <h2 className="text-xl font-black text-white">Add to Your Phone</h2>
            <p className="text-sm text-white/80 mt-1">Get YAS Connect on your home screen</p>
          </div>

          <div className="p-5 space-y-4">
            {/* Benefits */}
            <div className="space-y-2">
              {[
                'Get push notifications from your mentor',
                'One tap to open — right from your home screen',
                'Works like a real app on your phone',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <p className="text-sm text-gray-700">{item}</p>
                </div>
              ))}
            </div>

            {/* Platform-specific instructions */}
            {platform === 'ios' ? (
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-100 space-y-4">
                <p className="text-xs font-bold text-gray-800 text-center">iPhone Instructions</p>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">1</div>
                  <div>
                    <p className="text-sm text-gray-700 font-medium">Tap the Share button</p>
                    <p className="text-xs text-gray-400 mt-0.5">The square with arrow icon at the bottom of Safari</p>
                    <div className="mt-2 w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">2</div>
                  <div>
                    <p className="text-sm text-gray-700 font-medium">Scroll down and tap</p>
                    <div className="mt-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-gray-200">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                      <span className="text-sm font-medium text-gray-700">Add to Home Screen</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">3</div>
                  <div>
                    <p className="text-sm text-gray-700 font-medium">Tap <strong>Add</strong> in the top right corner</p>
                  </div>
                </div>
              </div>
            ) : deferredPrompt ? (
              /* Android with native install prompt available */
              <Button onClick={handleInstall} disabled={installing}
                className="w-full py-5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-base shadow-lg shadow-orange-500/20"
                data-testid="install-btn">
                {installing ? 'Installing...' : 'Install YAS Connect'}
                <ArrowDown className="w-5 h-5 ml-2" />
              </Button>
            ) : (
              /* Android without native prompt (manual instructions) */
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-100 space-y-4">
                <p className="text-xs font-bold text-gray-800 text-center">Android Instructions</p>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">1</div>
                  <div>
                    <p className="text-sm text-gray-700 font-medium">Tap the menu icon</p>
                    <p className="text-xs text-gray-400 mt-0.5">The three dots at the top right of Chrome</p>
                    <div className="mt-2 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="#374151"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">2</div>
                  <div>
                    <p className="text-sm text-gray-700 font-medium">Tap <strong>Add to Home screen</strong></p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">3</div>
                  <div>
                    <p className="text-sm text-gray-700 font-medium">Tap <strong>Install</strong></p>
                  </div>
                </div>
              </div>
            )}

            <button onClick={dismissForNow} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 py-2" data-testid="dismiss-install">
              I will do this later
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InstallPrompt;
